import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from './axiosConfig';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
  Tooltip
} from '@mui/material';
import {
  Home as HomeIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  CalendarMonth as CalendarIcon,
  AccessTime as ClockIcon,
  BeachAccess as HolidayIcon,
  LocalHospital as SickIcon,
  Payments as PayIcon,
  TrendingUp as CommissionIcon,
  Close as CloseIcon
} from '@mui/icons-material';

const PayrollExport = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [payrollData, setPayrollData] = useState(null);
  const [error, setError] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeDetail, setEmployeeDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  
  // Period selection
  const [periodType, setPeriodType] = useState('weekly');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  
  // Calculate default dates
  const getDefaultDates = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const daysUntilSunday = (7 - dayOfWeek) % 7;
    const endDate = new Date(today);
    endDate.setDate(today.getDate() + daysUntilSunday);
    const startDate = new Date(endDate);
    startDate.setDate(endDate.getDate() - 6);
    
    return {
      start: startDate.toISOString().split('T')[0],
      end: endDate.toISOString().split('T')[0]
    };
  };

  useEffect(() => {
    const defaults = getDefaultDates();
    setCustomStart(defaults.start);
    setCustomEnd(defaults.end);
    fetchPayrollData();
  }, []);

  const fetchPayrollData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      params.append('period', periodType);
      if (periodType === 'custom' && customStart && customEnd) {
        params.append('start_date', customStart);
        params.append('end_date', customEnd);
      }
      
      const response = await api.get(`/payroll-export/preview?${params.toString()}`);
      setPayrollData(response.data);
    } catch (err) {
      console.error('Error fetching payroll data:', err);
      setError(err.response?.data?.error || 'Failed to load payroll data');
    } finally {
      setLoading(false);
    }
  };

  const handlePeriodChange = (e) => {
    setPeriodType(e.target.value);
  };

  const handleRefresh = () => {
    fetchPayrollData();
  };

  const handleExport = async (format) => {
    try {
      const params = new URLSearchParams();
      params.append('period', periodType);
      if (periodType === 'custom' && customStart && customEnd) {
        params.append('start_date', customStart);
        params.append('end_date', customEnd);
      }
      
      let endpoint = '/payroll-export/export';
      if (format === 'sage') endpoint = '/payroll-export/export/sage';
      if (format === 'xero') endpoint = '/payroll-export/export/xero';
      
      const response = await api.get(`${endpoint}?${params.toString()}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from header or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = `payroll_export.csv`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting payroll:', err);
      setError('Failed to export payroll data');
    }
  };

  const handleViewEmployee = async (employeeId) => {
    setSelectedEmployee(employeeId);
    setDetailLoading(true);
    
    try {
      const params = new URLSearchParams();
      params.append('period', periodType);
      if (periodType === 'custom' && customStart && customEnd) {
        params.append('start_date', customStart);
        params.append('end_date', customEnd);
      }
      
      const response = await api.get(`/payroll-export/employee/${employeeId}?${params.toString()}`);
      setEmployeeDetail(response.data);
    } catch (err) {
      console.error('Error fetching employee detail:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(value || 0);
  };

  const formatHours = (value) => {
    return `${(value || 0).toFixed(1)}h`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Paper 
        elevation={0} 
        sx={{ 
          background: 'linear-gradient(135deg, #7c3aed, #6366f1)', 
          color: 'white', 
          p: 4, 
          mb: 4, 
          borderRadius: 0 
        }}
      >
        <Container maxWidth="lg">
          <Button 
            startIcon={<HomeIcon />} 
            onClick={() => navigate('/time-dashboard')} 
            sx={{ color: 'white', mb: 2, '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }}}
          >
            Back to Time & Attendance
          </Button>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            Payroll Export
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9 }}>
            Generate payroll data from clock entries, leave, and commissions
          </Typography>
        </Container>
      </Paper>

      <Container maxWidth="lg">
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Controls */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Pay Period</InputLabel>
                <Select
                  value={periodType}
                  label="Pay Period"
                  onChange={handlePeriodChange}
                >
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="fortnightly">Fortnightly</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                  <MenuItem value="custom">Custom Range</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {periodType === 'custom' && (
              <>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    type="date"
                    label="Start Date"
                    value={customStart}
                    onChange={(e) => setCustomStart(e.target.value)}
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    type="date"
                    label="End Date"
                    value={customEnd}
                    onChange={(e) => setCustomEnd(e.target.value)}
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </>
            )}
            
            <Grid item xs={12} sm={6} md={periodType === 'custom' ? 2 : 3}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                fullWidth
              >
                Refresh
              </Button>
            </Grid>
            
            <Grid item xs={12} sm={6} md={periodType === 'custom' ? 3 : 6}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleExport('generic')}
                  sx={{ background: 'linear-gradient(135deg, #7c3aed, #6366f1)' }}
                >
                  Export CSV
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleExport('sage')}
                  color="success"
                >
                  Sage
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleExport('xero')}
                  color="info"
                >
                  Xero
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Period Info */}
        {payrollData?.period && (
          <Paper sx={{ p: 2, mb: 3, bgcolor: '#f8fafc' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CalendarIcon color="primary" />
              <Typography variant="h6">
                {payrollData.period.description}
              </Typography>
              <Chip 
                label={`${payrollData.totals.employees} employees`} 
                size="small" 
                color="primary" 
                variant="outlined" 
              />
            </Box>
          </Paper>
        )}

        {/* Summary Cards */}
        {payrollData?.totals && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={4} md={2}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <ClockIcon sx={{ color: '#3b82f6', fontSize: 28, mb: 1 }} />
                  <Typography variant="h5" fontWeight="bold">
                    {formatHours(payrollData.totals.total_hours)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Total Hours
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <ClockIcon sx={{ color: '#f59e0b', fontSize: 28, mb: 1 }} />
                  <Typography variant="h5" fontWeight="bold">
                    {formatHours(payrollData.totals.overtime_hours)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Overtime
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <HolidayIcon sx={{ color: '#10b981', fontSize: 28, mb: 1 }} />
                  <Typography variant="h5" fontWeight="bold">
                    {payrollData.totals.holiday_days}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Holiday Days
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <SickIcon sx={{ color: '#ef4444', fontSize: 28, mb: 1 }} />
                  <Typography variant="h5" fontWeight="bold">
                    {payrollData.totals.sick_days}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Sick Days
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <CommissionIcon sx={{ color: '#8b5cf6', fontSize: 28, mb: 1 }} />
                  <Typography variant="h5" fontWeight="bold">
                    {formatCurrency(payrollData.totals.commission)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Commission
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Card sx={{ height: '100%', bgcolor: '#7c3aed', color: 'white' }}>
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <PayIcon sx={{ fontSize: 28, mb: 1 }} />
                  <Typography variant="h5" fontWeight="bold">
                    {formatCurrency(payrollData.totals.gross_pay)}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    Gross Pay
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Employee Table */}
        <Paper sx={{ mb: 3 }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Employee</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Days</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Hours</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>OT</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Holiday</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Sick</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Basic</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>OT Pay</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Hol Pay</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Comm</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>SSP</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: '#7c3aed' }}>Gross</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payrollData?.employees?.map((emp) => (
                  <TableRow 
                    key={emp.employee_id}
                    hover
                    sx={{ 
                      '&:hover': { bgcolor: '#f8fafc' },
                      bgcolor: emp.late_count > 0 ? '#fffbeb' : 'inherit'
                    }}
                  >
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {emp.full_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {emp.job_role}
                          {emp.payroll_id && ` • ${emp.payroll_id}`}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">{emp.days_worked}</TableCell>
                    <TableCell align="center">{emp.hours_worked.toFixed(1)}</TableCell>
                    <TableCell align="center">
                      {emp.overtime_hours > 0 && (
                        <Chip 
                          label={emp.overtime_hours.toFixed(1)} 
                          size="small" 
                          color="warning" 
                          variant="outlined" 
                        />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {emp.holiday_days > 0 && (
                        <Chip 
                          label={emp.holiday_days} 
                          size="small" 
                          color="success" 
                          variant="outlined" 
                        />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {emp.sick_days > 0 && (
                        <Chip 
                          label={emp.sick_days} 
                          size="small" 
                          color="error" 
                          variant="outlined" 
                        />
                      )}
                    </TableCell>
                    <TableCell align="right">{formatCurrency(emp.basic_pay)}</TableCell>
                    <TableCell align="right">
                      {emp.overtime_pay > 0 ? formatCurrency(emp.overtime_pay) : '-'}
                    </TableCell>
                    <TableCell align="right">
                      {emp.holiday_pay > 0 ? formatCurrency(emp.holiday_pay) : '-'}
                    </TableCell>
                    <TableCell align="right">
                      {(emp.service_commission + emp.product_commission) > 0 
                        ? formatCurrency(emp.service_commission + emp.product_commission) 
                        : '-'}
                    </TableCell>
                    <TableCell align="right">
                      {emp.ssp_amount > 0 ? formatCurrency(emp.ssp_amount) : '-'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: '#7c3aed' }}>
                      {formatCurrency(emp.gross_pay)}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="View Details">
                        <IconButton 
                          size="small" 
                          onClick={() => handleViewEmployee(emp.employee_id)}
                        >
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                
                {/* Totals Row */}
                {payrollData?.totals && (
                  <TableRow sx={{ bgcolor: '#f1f5f9' }}>
                    <TableCell sx={{ fontWeight: 700 }}>
                      TOTALS ({payrollData.totals.employees} employees)
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>-</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>
                      {payrollData.totals.total_hours.toFixed(1)}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>
                      {payrollData.totals.overtime_hours.toFixed(1)}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>
                      {payrollData.totals.holiday_days}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>
                      {payrollData.totals.sick_days}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {formatCurrency(payrollData.totals.basic_pay)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {formatCurrency(payrollData.totals.overtime_pay)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {formatCurrency(payrollData.totals.holiday_pay)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {formatCurrency(payrollData.totals.commission)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {formatCurrency(payrollData.totals.ssp)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: '#7c3aed', fontSize: '1rem' }}>
                      {formatCurrency(payrollData.totals.gross_pay)}
                    </TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Info Box */}
        <Paper sx={{ p: 3, bgcolor: '#eff6ff', border: '1px solid #bfdbfe' }}>
          <Typography variant="subtitle2" color="primary" gutterBottom>
            Export Information
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • <strong>Generic CSV</strong> - Standard format compatible with most payroll software<br/>
            • <strong>Sage</strong> - Formatted for Sage 50 Payroll import<br/>
            • <strong>Xero</strong> - Formatted for Xero Payroll import<br/>
            • SSP calculated using your configured statutory rate, from day 1 of sickness<br/>
            • Overtime calculated using your configured overtime multiplier<br/>
            • Holiday pay calculated using
          </Typography>
        </Paper>
      </Container>

      {/* Employee Detail Dialog */}
      <Dialog 
        open={selectedEmployee !== null} 
        onClose={() => setSelectedEmployee(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {employeeDetail?.employee?.name || 'Employee'} - Payroll Detail
            </Typography>
            <IconButton onClick={() => setSelectedEmployee(null)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : employeeDetail ? (
            <Box>
              {/* Employee Info */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Job Role</Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {employeeDetail.employee.job_role || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Hourly Rate</Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {formatCurrency(employeeDetail.employee.hourly_rate)}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Payroll ID</Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {employeeDetail.employee.payroll_id || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">NI Number</Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {employeeDetail.employee.ni_number || '-'}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Clock Entries */}
              <Typography variant="subtitle2" gutterBottom>
                Clock Entries ({employeeDetail.clock_entries?.length || 0})
              </Typography>
              {employeeDetail.clock_entries?.length > 0 ? (
                <TableContainer sx={{ mb: 3 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Clock In</TableCell>
                        <TableCell>Clock Out</TableCell>
                        <TableCell align="right">Hours</TableCell>
                        <TableCell align="right">OT</TableCell>
                        <TableCell align="right">Break</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {employeeDetail.clock_entries.map((entry, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            {new Date(entry.date).toLocaleDateString('en-GB', {
                              weekday: 'short',
                              day: 'numeric',
                              month: 'short'
                            })}
                          </TableCell>
                          <TableCell>{entry.clock_in?.slice(0, 5) || '-'}</TableCell>
                          <TableCell>{entry.clock_out?.slice(0, 5) || '-'}</TableCell>
                          <TableCell align="right">{entry.hours.toFixed(1)}h</TableCell>
                          <TableCell align="right">
                            {entry.overtime > 0 && (
                              <Chip label={`${entry.overtime.toFixed(1)}h`} size="small" color="warning" />
                            )}
                          </TableCell>
                          <TableCell align="right">{entry.break_minutes}m</TableCell>
                          <TableCell>
                            {entry.late && <Chip label="Late" size="small" color="warning" sx={{ mr: 0.5 }} />}
                            {entry.early && <Chip label="Early" size="small" color="error" />}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  No clock entries for this period
                </Typography>
              )}

              {/* Leave Entries */}
              {employeeDetail.leave_entries?.length > 0 && (
                <>
                  <Typography variant="subtitle2" gutterBottom>
                    Leave Taken
                  </Typography>
                  <TableContainer sx={{ mb: 3 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Type</TableCell>
                          <TableCell>Start</TableCell>
                          <TableCell>End</TableCell>
                          <TableCell align="right">Days</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {employeeDetail.leave_entries.map((entry, idx) => (
                          <TableRow key={idx}>
                            <TableCell>
                              <Chip 
                                label={entry.type.replace('_', ' ')} 
                                size="small"
                                color={entry.type.includes('SICK') ? 'error' : 'success'}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              {new Date(entry.start_date).toLocaleDateString('en-GB')}
                            </TableCell>
                            <TableCell>
                              {new Date(entry.end_date).toLocaleDateString('en-GB')}
                            </TableCell>
                            <TableCell align="right">{entry.days}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Pay Summary */}
              <Typography variant="subtitle2" gutterBottom>
                Pay Summary
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={4}>
                  <Paper sx={{ p: 2, bgcolor: '#f8fafc' }}>
                    <Typography variant="caption" color="text.secondary">Basic Pay</Typography>
                    <Typography variant="h6">
                      {formatCurrency(employeeDetail.summary?.basic_pay)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {employeeDetail.summary?.hours_worked?.toFixed(1)}h × {formatCurrency(employeeDetail.employee.hourly_rate)}/hr
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Paper sx={{ p: 2, bgcolor: '#fef3c7' }}>
                    <Typography variant="caption" color="text.secondary">Overtime Pay</Typography>
                    <Typography variant="h6">
                      {formatCurrency(employeeDetail.summary?.overtime_pay)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {employeeDetail.summary?.overtime_hours?.toFixed(1)}h @ 1.5x
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Paper sx={{ p: 2, bgcolor: '#dcfce7' }}>
                    <Typography variant="caption" color="text.secondary">Holiday Pay</Typography>
                    <Typography variant="h6">
                      {formatCurrency(employeeDetail.summary?.holiday_pay)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {employeeDetail.summary?.holiday_days} days
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Paper sx={{ p: 2, bgcolor: '#ede9fe' }}>
                    <Typography variant="caption" color="text.secondary">Commission</Typography>
                    <Typography variant="h6">
                      {formatCurrency((employeeDetail.summary?.service_commission || 0) + (employeeDetail.summary?.product_commission || 0))}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Paper sx={{ p: 2, bgcolor: '#fee2e2' }}>
                    <Typography variant="caption" color="text.secondary">SSP</Typography>
                    <Typography variant="h6">
                      {formatCurrency(employeeDetail.summary?.ssp_amount)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {employeeDetail.summary?.ssp_days} qualifying days
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Paper sx={{ p: 2, bgcolor: '#7c3aed', color: 'white' }}>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>Gross Pay</Typography>
                    <Typography variant="h5" fontWeight="bold">
                      {formatCurrency(employeeDetail.summary?.gross_pay)}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedEmployee(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PayrollExport;